/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Button,
  ButtonGroup,
  ButtonVariety,
  Heading,
  HeadingSize,
  Text,
} from '@sonarsource/echoes-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { NumberedList, NumberedListItem } from '~sq-server-commons/design-system';
import { useCreateIntegrationConfigurationMutation } from '~sq-server-commons/queries/integrations';
import { IntegrationType } from '~sq-server-commons/types/integrations';
import { SlackIntegrationConfigurationForm } from './SlackIntegrationConfigurationForm';

interface SlackIntegrationConfigurationCreationProps {
  onHideForm: () => void;
  slackAppManifestUrl: string;
}

export function SlackIntegrationConfigurationCreation({
  onHideForm,
  slackAppManifestUrl,
}: Readonly<SlackIntegrationConfigurationCreationProps>) {
  const [slackConfiguration, setSlackConfiguration] = useState<{
    clientId: string;
    clientSecret: string;
    signingSecret: string;
  }>({
    clientId: '',
    clientSecret: '',
    signingSecret: '',
  });

  const { isPending: isCreatingSlackConfiguration, mutate: createSlackConfiguration } =
    useCreateIntegrationConfigurationMutation();

  const isSubmitDisabled =
    isCreatingSlackConfiguration ||
    slackConfiguration.clientId === '' ||
    slackConfiguration.clientSecret === '' ||
    slackConfiguration.signingSecret === '';

  const onCancel = () => {
    setSlackConfiguration({
      clientId: '',
      clientSecret: '',
      signingSecret: '',
    });
    onHideForm();
  };
  const onSubmit = () => {
    if (isSubmitDisabled) {
      return;
    }

    createSlackConfiguration({
      ...slackConfiguration,
      integrationType: IntegrationType.Slack,
    });
  };

  return (
    <NumberedList className="sw-flex sw-flex-col sw-gap-10">
      <NumberedListItem className="sw-flex sw-gap-1">
        <div>
          <div className="sw-flex sw-flex-col sw-gap-4">
            <Heading as="h3" size={HeadingSize.Medium}>
              <FormattedMessage id="settings.slack.app_creation.step_title" />
            </Heading>
            <Text>
              <FormattedMessage id="settings.slack.app_creation.helper" />
            </Text>
          </div>
          <div className="sw-flex sw-items-center sw-mt-6 sw-gap-3">
            <Button enableOpenInNewTab to={slackAppManifestUrl} variety={ButtonVariety.Default}>
              <FormattedMessage id="settings.slack.app_creation.button_label" />
            </Button>
            <Text isSubtle>
              <FormattedMessage id="settings.slack.app_creation.hint" />
            </Text>
          </div>
        </div>
      </NumberedListItem>
      <NumberedListItem className="sw-flex sw-gap-1">
        <div>
          <div className="sw-flex sw-flex-col sw-gap-4">
            <Heading as="h3" size={HeadingSize.Medium}>
              <FormattedMessage id="settings.slack.app_details.step_title" />
            </Heading>
            <SlackIntegrationConfigurationForm
              setSlackConfiguration={setSlackConfiguration}
              slackConfiguration={slackConfiguration}
            />
          </div>
          <ButtonGroup className="sw-flex sw-items-center sw-mt-6 sw-gap-2">
            <Button
              isDisabled={isSubmitDisabled}
              isLoading={isCreatingSlackConfiguration}
              onClick={onSubmit}
              variety={ButtonVariety.Primary}
            >
              <FormattedMessage id="settings.slack.app_details.submit_button_label" />
            </Button>
            <Button
              isDisabled={isCreatingSlackConfiguration}
              onClick={onCancel}
              variety={ButtonVariety.Default}
            >
              <FormattedMessage id="cancel" />
            </Button>
          </ButtonGroup>
        </div>
      </NumberedListItem>
    </NumberedList>
  );
}
